import { apiClient } from './apiClient';
import { useAuthStore } from '../state';
const BASE_PATH = '/api/v1/shipments';
/**
 * Get all documents for a shipment
 */
export const getShipmentDocuments = async (shipmentId, params) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents`, { params });
    return response.data;
};
/**
 * Get a single document
 */
export const getShipmentDocument = async (shipmentId, documentId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents/${documentId}`);
    return response.data.data;
};
/**
 * Upload documents to a shipment
 */
export const uploadShipmentDocuments = async (shipmentId, files, metadata) => {
    const formData = new FormData();
    // Handle single file upload - backend expects 'file' field for single uploads
    if (files.length === 1) {
        formData.append('file', files[0]);
        if (metadata && metadata[0]) {
            // Add metadata fields directly to formData for single file
            Object.entries(metadata[0]).forEach(([key, value]) => {
                if (value !== undefined) {
                    formData.append(key, String(value));
                }
            });
        }
    }
    else {
        // Handle multiple files - use 'files' field
        files.forEach(file => {
            formData.append('files', file);
        });
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }
    }
    // Debug logging
    console.log('Uploading to shipment:', shipmentId);
    console.log('Number of files:', files.length);
    console.log('FormData entries:');
    for (const [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value);
    }
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/documents`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    // Handle response - single upload returns data directly, multiple returns array
    if (files.length === 1 && response.data.data && !Array.isArray(response.data.data)) {
        return [response.data.data];
    }
    return response.data.data;
};
/**
 * Update document metadata
 */
export const updateShipmentDocument = async (shipmentId, documentId, data) => {
    const response = await apiClient.put(`${BASE_PATH}/${shipmentId}/documents/${documentId}`, data);
    return response.data;
};
/**
 * Delete a document
 */
export const deleteShipmentDocument = async (shipmentId, documentId) => {
    await apiClient.delete(`${BASE_PATH}/${shipmentId}/documents/${documentId}`);
};
/**
 * Get document download URL
 */
export const getDocumentDownloadUrl = (shipmentId, documentId) => {
    const apiUrl = useAuthStore.getState().apiUrl;
    return `${apiUrl}${BASE_PATH}/${shipmentId}/documents/${documentId}/download`;
};
/**
 * Extract data from a document
 */
export const extractDocument = async (shipmentId, documentId, options) => {
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/documents/${documentId}/extract`, options);
    return response.data;
};
/**
 * Get extraction status
 */
export const getExtractionStatus = async (shipmentId, jobId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/extraction-jobs/${jobId}`);
    return response.data;
};
/**
 * Get extraction result
 */
export const getExtractionResult = async (shipmentId, documentId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents/${documentId}/extraction`);
    return response.data;
};
/**
 * Update extracted data
 */
export const updateExtractedData = async (shipmentId, documentId, data, changes) => {
    const response = await apiClient.put(`${BASE_PATH}/${shipmentId}/documents/${documentId}/extracted-data`, {
        data,
        changes,
    });
    return response.data;
};
/**
 * Validate documents
 */
export const validateShipmentDocuments = async (shipmentId, documentIds) => {
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/validate`, { documentIds });
    return response.data;
};
/**
 * Get missing documents
 */
export const getMissingDocuments = async (shipmentId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/missing-documents`);
    return response.data.data;
};
/**
 * Apply validation remediation
 */
export const applyRemediation = async (shipmentId, issueId, remediationId) => {
    await apiClient.post(`${BASE_PATH}/${shipmentId}/validation-issues/${issueId}/remediate`, { remediationId });
};
/**
 * Cross-validate documents (creates a background job)
 */
export const crossValidateDocuments = async (shipmentId, documentIds) => {
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/documents/cross-validate`, { documentIds });
    return response.data;
};
/**
 * Merge documents (creates a background job)
 */
export const mergeDocuments = async (shipmentId, documentIds) => {
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/documents/merge`, { documentIds });
    return response.data;
};
/**
 * Get document history
 */
export const getDocumentHistory = async (shipmentId, documentId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents/${documentId}/history`);
    return response.data;
};
/**
 * Track manual correction
 */
export const trackManualCorrection = async (documentId, fieldPath, oldValue, newValue) => {
    await apiClient.post(`/api/v1/documents/${documentId}/corrections`, { fieldPath, oldValue, newValue });
};
/**
 * Guess document type from filename using AI
 */
export const guessDocumentType = async (shipmentId, fileName) => {
    const response = await apiClient.post(`${BASE_PATH}/${shipmentId}/documents/guess-type`, { fileName });
    return response.data;
};
/**
 * Get cross-validation job status
 */
export const getCrossValidationJobStatus = async (shipmentId, jobId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents/cross-validate/${jobId}`);
    return response.data;
};
/**
 * Get merge job status
 */
export const getMergeJobStatus = async (shipmentId, jobId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/documents/merge/${jobId}`);
    return response.data;
};
/**
 * Get all jobs for a shipment
 */
export const getShipmentJobs = async (shipmentId) => {
    const response = await apiClient.get(`${BASE_PATH}/${shipmentId}/jobs`);
    return response.data;
};
